import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PlusOutlined } from '@ant-design/icons/lib';
import styled from 'styled-components';

import { createView, duplicateBatchView, linkView } from 'apis/ViewAPI';
import AddViewTemplatesModal from 'components/View/modals/AddViewTemplatesModal/AddViewTemplatesModal';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { hasPermissionOrAdmin, isManagedBySuperuser } from 'helpers/permissions';
import { VIEW_PERMISSIONS } from 'permissions/views';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleStoreViews } from 'store/slices/globalSlice';

import { STATUS } from '../../../constants';
import { VIEW_MODAL_ADD_COMMUNITY_TYPES, VIEW_MODALS, VIEW_TYPE } from '../../View/constants';
import { sortViews } from '../../View/utils';
import { useRootContext } from '../RootContext';

const Container = styled.div`
  font-size: 14px;
`;

const Text = styled.span`
  margin-left: 5px;
  font-size: 12px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: end;
  cursor: pointer;
`;

const SidebarMenuAddView = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { views, selectedCommunity, selectedCompany, selectedParentCommunity } = useAppSelector(
    (state) => state.global
  );
  const hasPricingDataAccess = Boolean(currentUser?.feature_flags?.view_pricing_data);
  const isAdminUser = Boolean(currentUser?.isSuperuser || isManagedBySuperuser(currentUser));
  const canCreateFromTemplate = isAdminUser || hasPricingDataAccess;

  const [open, setOpen] = useState<boolean>(false);
  const [createStatus, setCreateStatus] = useState<string>(STATUS.IDLE);
  const [communityContractType, setCommunityContractType] = useState<number>(VIEW_MODAL_ADD_COMMUNITY_TYPES.RENTAL);

  const createFromScratch = (viewName: string) => {
    const handleCreateView = async () => {
      return await createView({
        name: viewName,
        community_contract_type_ids:
          communityContractType === VIEW_MODAL_ADD_COMMUNITY_TYPES.BOTH
            ? [VIEW_MODAL_ADD_COMMUNITY_TYPES.RENTAL, VIEW_MODAL_ADD_COMMUNITY_TYPES.CCRC]
            : [communityContractType],
        type: VIEW_TYPE.PUBLIC,
      });
    };
    setCreateStatus(STATUS.LOADING);
    return handleCreateView()
      .then((response) => {
        dispatch(handleStoreViews([...views, response]));
        showMessage('success', 'New view created.');
        setOpen(false);
        setCreateStatus(STATUS.LOADED);
        const viewId = response.id;

        if (selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR) {
          navigate(`/competitor/${selectedCommunity?.id}/view/${viewId}`);
        } else {
          navigate(`/community/${selectedCommunity?.id}/view/${viewId}`);
        }
      })
      .catch((reason) => {
        setCreateStatus(STATUS.FAILURE);
        showMessage('error', 'Trouble creating company related view.');
      });
  };

  if (!hasPermissionOrAdmin(currentUser, [VIEW_PERMISSIONS.CUSTOM.ADD])) {
    return null;
  }

  const createFromTemplate = async (duplicateViews: any[]) => {
    setCreateStatus(STATUS.LOADING);
    return await duplicateBatchView({ company_id: selectedCompany?.id, views: duplicateViews })
      .then(async (viewResponse: any) => {
        const { duplicated_views } = viewResponse;
        const sortedViews = [...views, ...duplicated_views].sort(sortViews);
        dispatch(handleStoreViews(sortedViews));
        setCreateStatus(STATUS.LOADED);
        showMessage('success', 'Duplicated view created.');
        setOpen(false);
      })
      .catch((reason: any) => {
        setCreateStatus(STATUS.FAILURE);
        showMessage('error', 'Trouble duplicating view.');
      });
  };

  return (
    <Container>
      <AddViewTemplatesModal
        open={open}
        type={VIEW_MODALS.ADD}
        loading={createStatus === STATUS.LOADING}
        communityContractType={communityContractType}
        setCommunityContractType={setCommunityContractType}
        canCreateFromTemplate={canCreateFromTemplate}
        createFromTemplate={createFromTemplate}
        createFromScratch={createFromScratch}
        onCancel={() => setOpen(false)}
      />
      <Wrapper onClick={() => setOpen(true)}>
        <PlusOutlined style={{ fontSize: '10px', padding: '4px' }} />
        <Text>Add View...</Text>
      </Wrapper>
    </Container>
  );
};

export default SidebarMenuAddView;
